import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Circle, Clock, PencilRuler, CheckCircle2, ChevronRight, Radio } from 'lucide-react';
import dayjs from 'dayjs';
import type { XkEvent } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { queryKeys } from '@/lib/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { factsApi } from '@/features/insights/factsApi';
import { fmtInt, goalOf, ledgerOf, paceOf, windowFraction } from '@/features/insights/engine';
import { GoalCounters } from '@/features/insights/charts/GoalCounters';
import { PrizeWall } from '@/features/insights/charts/PrizeWall';
import { eventPhase, type EventPhase } from '../eventPhase';

/**
 * TRACK tab — every event grouped by phase (live / upcoming / edit / done).
 * Clicking an editable event jumps to the PLAN tab focused on its collapse.
 */
export function TrackTab({
  groupId,
  events,
  onJumpToPlan,
}: {
  groupId: number;
  events: XkEvent[];
  onJumpToPlan: (eventId: number) => void;
}) {
  const t = useT();

  const groups = useMemo(() => {
    const acc: Record<EventPhase, XkEvent[]> = { live: [], upcoming: [], edit: [], done: [] };
    for (const e of events) acc[eventPhase(e)].push(e);
    return acc;
  }, [events]);

  const order: EventPhase[] = ['live', 'upcoming', 'edit', 'done'];

  if (events.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] p-6 text-center text-sm text-[var(--color-xk-text-muted)]">
        {t('track.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Track sigue al evento en directo: un jefe de campo está en UNA
          estación. Si hay uno vivo, sus contadores y su muro van arriba; si
          no, se dice, en vez de enseñar un evento congelado como si viviera. */}
      <LiveFollow groupId={groupId} live={groups.live} />
      {order.map((phase) => (
        <TrackGroup
          key={phase}
          phase={phase}
          events={groups[phase]}
          groupId={groupId}
          onJumpToPlan={onJumpToPlan}
        />
      ))}
    </div>
  );
}

function LiveFollow({ groupId, live }: { groupId: number; live: XkEvent[] }) {
  const t = useT();
  const navigate = useNavigate();
  const target = live[0];
  const ids = useMemo(() => (target ? [target.id] : []), [target]);
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts(ids),
    queryFn: () => factsApi.forEvents(ids),
    enabled: ids.length > 0,
    refetchInterval: 15_000,
  });
  if (!target) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5">
        <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-fg-subtle)]">
          {t('projectTrack.kicker')}
        </div>
        <p className="text-sm leading-relaxed text-[var(--color-fg-muted)]">{t('projectTrack.nothingLive')}</p>
      </div>
    );
  }
  const facts = factsQ.data?.events[0];
  const ledger = facts ? ledgerOf(facts.ledger) : null;
  const frac = facts ? windowFraction(facts.event.startDate, facts.event.endDate) : null;
  return (
    <section className="space-y-4 rounded-2xl border border-[var(--color-xk-accent)] bg-[var(--color-bg-surface)] p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-accent-ink)]">
            <Radio className="h-3.5 w-3.5 animate-pulse" />
            {t('projectTrack.followingLive')}
          </div>
          <h3 className="text-xl font-bold text-[var(--color-fg-primary)]">{target.name}</h3>
          {live.length > 1 && (
            <p className="text-xs text-[var(--color-fg-muted)]">{t('projectTrack.otherLive', { n: live.length - 1 })}</p>
          )}
        </div>
        <Button variant="secondary" onClick={() => navigate(`/projects/${groupId}/events/${target.id}?tab=track`)}>
          {t('projectTrack.openTrack')}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      {!facts || !ledger ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          <GoalCounters
            counters={[
              {
                label: t('liveMonitor.kpi.interactions'),
                pace: paceOf(facts.funnel.interactions, goalOf(facts, 'responses') ?? goalOf(facts, 'samples'), { frac, live: true }),
                hint: t('liveMonitor.hint.interactions'),
              },
              {
                label: t('liveMonitor.kpi.leads'),
                pace: paceOf(facts.funnel.leads, goalOf(facts, 'leads'), { frac, live: true }),
                hint: t('liveMonitor.hint.leads'),
              },
              {
                label: t('liveMonitor.kpi.prizesDistributed'),
                pace: paceOf(ledger.delivered, ledger.won, { mode: 'ratio' }),
                hint: t('liveMonitor.hint.prizes', { inPlay: fmtInt(ledger.inPlay), allocated: fmtInt(ledger.allocated) }),
                mode: 'ratio',
              },
            ]}
          />
          <PrizeWall rows={ledger.rows} />
        </>
      )}
    </section>
  );
}

const PHASE_META: Record<EventPhase, { icon: typeof Circle; color: string }> = {
  live: { icon: Circle, color: 'var(--color-xk-accent)' },
  upcoming: { icon: Clock, color: 'var(--color-warn)' },
  edit: { icon: PencilRuler, color: 'var(--color-xk-lavender)' },
  done: { icon: CheckCircle2, color: 'var(--color-xk-text-muted)' },
};

function TrackGroup({
  phase,
  events,
  groupId,
  onJumpToPlan,
}: {
  phase: EventPhase;
  events: XkEvent[];
  groupId: number;
  onJumpToPlan: (eventId: number) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const meta = PHASE_META[phase];
  const Icon = meta.icon;
  const editable = phase === 'edit' || phase === 'upcoming';

  if (events.length === 0) return null;

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: meta.color }} />
        <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t(`track.group.${phase}`)} · {events.length}
        </h3>
      </div>
      <div className="space-y-2">
        {events.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() =>
              editable ? onJumpToPlan(e.id) : navigate(`/projects/${groupId}/events/${e.id}`)
            }
            className={cn(
              'flex w-full items-center justify-between gap-4 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-4 text-left transition-colors hover:border-[var(--color-accent-ink)]',
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-[var(--color-xk-lavender)]">{e.name}</p>
              <p className="truncate text-[11px] text-[var(--color-xk-text-muted)] tabular-nums">
                {e.startDate ? dayjs(e.startDate).format('MMM D, YYYY h:mm A') : t('track.noDate')}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {editable ? t('track.edit') : t('track.view')}
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
